import { ButtonIcon, Layout, Typography } from '@sonnen/web-ui';
import clsx from 'clsx';
import { createElement, useEffect, useMemo, useRef, useState } from 'react';
import { useAppContext } from '../../AppContext/AppContext';
import { AppRoute } from '../../constants';
import { useCurrentRoute } from '../../hooks/useCurrentRoute';
import { useOrderFlowNavigation } from '../../hooks/useOrderFlowNavigation';
import BatteryOptimizationOfferCard from '../../Pages/BatteryOptimization/BatteryOptimizationOfferCard';
import { EnergyStoreSideCards } from '../../Pages/EnergyStore/EnergyStoreSideCards';
import SelfConsumptionOfferCard from '../../Pages/SelfConsumptionPlus/SelfConsumptionOfferCard/SelfConsumptionOfferCard';
import { OrderFlow } from '../../types';
import OfferCard from '../OfferCard/OfferCard';
import styles from './OrderFlowPage.module.css';
const sideCardByOrderFlow: Record<OrderFlow, React.ComponentType> = {
  [OrderFlow.batteryOptimization]: BatteryOptimizationOfferCard,
  [OrderFlow.selfConsumptionPlus]: SelfConsumptionOfferCard,
  [OrderFlow.energyFix]: OfferCard,
  [OrderFlow.energyDynamic]: OfferCard,
};

const sideCardByAppRoute: Partial<Record<AppRoute, React.ComponentType>> = {
  [AppRoute.ENERGY_STORE]: EnergyStoreSideCards,
  [AppRoute.ROOT]: EnergyStoreSideCards,
};

export type OrderFlowPageProps = {
  children: React.ReactNode;
  className?: string;
  dataTestId?: string;
  title?: string | React.ReactNode;
  /**
   * Optional side card component.
   * If not provided, it will default to a side card based on the current order flow or app route.
   * To not display any sidecard, pass `null`.
   */
  sideCard?: React.ComponentType | null;
};

/**
 * Render a page with optional side card and title.
 * @param props.children - The content to be displayed within the page.
 * @param props.className - Additional CSS classes to apply to the page container.
 * @param props.dataTestId - The data-testid attribute for testing purposes.
 * @param props.title - The title to be displayed at the top of the page.
 * @param props.sideCard - A React component to be rendered as a side card. If not provided, it will default to a side card based on the current order flow or app route.
 * If `null`, no side card will be rendered.
 */
export const OrderFlowPage = ({
  children,
  className,
  dataTestId = 'order-flow-page',
  title,
  sideCard,
}: OrderFlowPageProps) => {
  const { navigatePrevious } = useOrderFlowNavigation();
  const { orderFlow, userProcessing } = useAppContext();
  const currentRoute = useCurrentRoute();
  const [goBackBtnOpacity, setGoBackBtnOpacity] = useState(0.2);
  const currentMouseInterval = useRef<NodeJS.Timeout | null>(null);

  const showBackButton = () => {
    setGoBackBtnOpacity(1);
    if (currentMouseInterval.current) clearInterval(currentMouseInterval.current);
    currentMouseInterval.current = setTimeout(() => {
      setGoBackBtnOpacity(0.2);
    }, 1000);
  };

  const sideCardComponent = useMemo(() => {
    if (sideCard) return sideCard;
    if (sideCard === null) return null;
    return sideCardByAppRoute[currentRoute] || (orderFlow && sideCardByOrderFlow[orderFlow]);
  }, [currentRoute, orderFlow, sideCard]);

  useEffect(() => {
    if (navigatePrevious) {
      if (currentMouseInterval.current) clearInterval(currentMouseInterval.current);
      window.addEventListener('mousemove', showBackButton);
    }
    return () => {
      if (currentMouseInterval.current) clearInterval(currentMouseInterval.current);
      window.removeEventListener('mousemove', showBackButton);
    };
  }, [navigatePrevious]);

  return (
    <div
      className={clsx(styles.orderFlowPage, className, {
        [styles.minHeightWhenLoading]: userProcessing,
      })}
      data-testid={dataTestId}
    >
      <Layout centerVertically backgroundColor="none">
        <div
          data-testid={`${dataTestId}-title`}
          className={clsx(styles.titleBar, {
            [styles.withPaddingBottom]: Boolean(title),
          })}
        >
          {navigatePrevious && (
            <div className={styles.navBtn} style={{ opacity: goBackBtnOpacity }}>
              <ButtonIcon
                dataTestId="go-back-btn"
                icon="ChevronLeft"
                ariaLabel="go-back"
                size="xl"
                onClick={navigatePrevious}
              />
            </div>
          )}

          {title && (
            <div className={styles.title}>
              <Typography.H1>{title}</Typography.H1>
            </div>
          )}
          <div />
        </div>

        {sideCardComponent ? (
          <div className={styles.formAndOfferLayout} data-testid="two-grid-layout">
            <div>{children}</div>
            <div>
              <span />
              <div className={styles.stickyContainer}>{createElement(sideCardComponent)}</div>
            </div>
          </div>
        ) : (
          children
        )}
      </Layout>
    </div>
  );
};
